import messagealert from '../../../assets/message-alert-square.svg'
import {
  DropDown,
  DropDownWithLabel,
  Input,
  InputWithLabel,
} from '../../../utils/UtilComponents'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import clsx from 'clsx'
import { fetchUserThresholds } from '../../CustomThresholds/thresholdsApi'
import {
  eventBodyTemplate,
  hazardProductMpa,
  sensorHazardProductMap,
  addUpdateEvent,
} from '../AlertingSlice/AlertingSlice'
import useErrorStatus from '../../../hooks/UseErrorStatus'
import { TWS_API_URL } from '../../../environment/apis.config'

type alertModalProps = {
  canShow : boolean, 
  updateModalState :  (isForm?: boolean, preFill?: any) => void, 
  parentSetter : (value: any) => void, 
  form? : boolean, 
  preFillData? : {}
}

const AlertModal = (
  { 
    canShow = false, 
    updateModalState, 
    parentSetter, 
    form = true, 
    preFillData = {} 
  } : alertModalProps) => {
  if (!canShow) return null  

  //Utility Function
  const getCurrentDateTimeLocal = (offset: number = 0) => {
    const now = new Date()
    now.setDate(now.getDate() + offset)
    // Pad with leading zeros
    const pad = (num: number) => num.toString().padStart(2, '0')
    const year = now.getFullYear()
    const month = pad(now.getMonth() + 1) // Months are 0-based
    const day = pad(now.getDate())
    const hours = pad(now.getHours())
    const minutes = pad(now.getMinutes())
    return `${year}-${month}-${day}T${hours}:${minutes}`
  }
  const getLocationList = (locationList: any) => {
    var resultList = {}
    for (let loc of locationList) {
      resultList[loc.name] = loc
    }
    return resultList
  }
  const notificationBox = useErrorStatus()

  // States to use across the modal
  const [eventName, setEventName] = useState('')

  const alertType = {
    'Model Based': 'model',
    'Model + Sensor Based': 'model + sensor',
  }
  const [selectedAlertType, setAlertType] = useState('model')

  const defaultLocationName = useSelector(
    (state) => state.user.defaultLocation.name
  )
  const locations = useSelector((state) => state.user.locations)

  const locationList = getLocationList(locations)

  const [currLocationValue, setLocationValue] = useState(
    locationList[defaultLocationName]
  )

  const [radius, setRadius] = useState<number>(10)
  const [notifiable, setNotifiable] = useState(useSelector((state) => state.user.email))
  const [startDate, setStartDate] = useState<string>(getCurrentDateTimeLocal())
  const [endDate, setEndDate] = useState<string>(getCurrentDateTimeLocal(1))

  // Setting states for all the hazard parameters
  const comparatorMapping = {
    'Lesser than': '<',
    'Lesser than or equal to': '<=',
    'Greater than': '>',
    'Greater than or equal to': '>=',
  }

  const paramMapping = {
    "Temperature": {
      label: 'Temperature',
      units: {
        Celcius: 'C',
        Farenheit: 'F',
      },
      comparators: comparatorMapping,
    },
    'Surface Winds': {
      label: 'Wind Speed',
      units: {
        Knots: 'kts',
        'Miles per Hour': 'mph',
      },
      comparators: comparatorMapping,
    },
    'Wind 250 FT': {
      label: 'Wind Speed 250 FT',
      units: {
        Knots: 'kts',
        'Miles per Hour': 'mph',
      },
      comparators: comparatorMapping,
    },
    'Surface Wind Gusts': {
      label: 'Wind Gust',
      units: {
        Knots: 'kts',
        'Miles per Hour': 'mph',
      },
      comparators: comparatorMapping,
    },
    // 'Pressure': {
    //     "label": 'Pressure',
    //     "units": {
    //         "Pascals": "Pa",
    //         "Hecto Pascal": "hPa"
    //     },
    //     "comparators": comparatorMapping,
    // },
    "Ceiling": {
      label: 'Ceiling',
      units: {
        Feet: 'ft',
        Meters: 'm',
      },
      comparators: comparatorMapping,
    },
    "Visibility": {
      label: 'Visibility',
      units: {
        SM: 'sm',
        Miles: 'mi',
      },
      comparators: comparatorMapping,
    },
  } 

  //Prefilling data for existing monitor
  function formatDateForInput(dateString : string) {
    const date = new Date(dateString);
    const offset = date.getTimezoneOffset();
    const local = new Date(date.getTime() - offset * 60 * 1000);
    return local.toISOString().slice(0, 16);
  }
 
  const processPreFillData = (monitorData = preFillData ) => {
    let distance;
    let processedData = {}
    let sensorBased = false
    const hazardUnitMap = {
      'feet' : "ft",
      'SM' : "sm",
      "kts" : 'kts',
     'degrees_f' : "F",
     'degrees_c' : "C"
    }
    let prodLocalMap = {
      "Temperature" : "Temperature",
      "Surface Winds" : "Surface Winds",
      "80M Winds" : "Wind 250 FT",
      "Surface Wind Gusts" : "Surface Wind Gusts",
      "Ceilings" : "Ceiling",
      "Visibility" : "Visibility",
      "WINDS_RTMA" : "Surface Winds",
      "TEMPERATURE" : "Temperature",
      "WINDS_80M" : "Wind 250 FT",
      "WINDGUSTS_RTMA" : "Surface Wind Gusts",
      "CEILINGS" : "Ceiling",
      "VISIBILITY" : "Visibility",
      "SENSOR_CEILINGS": 'Ceiling',
      "SENSOR_VISIBILITY": 'Visibility',
      "SENSOR_WINDS": 'Surface Winds',
      "SENSOR_WINDGUSTS": 'Surface Wind Gusts',
      "SENSOR_TEMPERATURE": 'Temperature',
    }
    
    for(let product of monitorData?.message?.properties?.products){
      if("hazard" in product){
        processedData[prodLocalMap[product["hazard"]]] = {
          "threshold": product["parameters"][0]["threshold"],
          "selectedComparator": product["parameters"][0]["comparison"],
          "unit": hazardUnitMap[product["parameters"][0]["thresholdUnits"]],
          "checked": true,
          "visible" : true 
        }
        distance = product["parameters"][0]["distance"] || 0
      }
      if ("hazard" in product && product["hazard"].toLowerCase().startsWith('sensor')) {sensorBased = true}
    }
    
    return [processedData, distance, sensorBased]
  }

  const [formData, setFormData] = useState(() => {
    let initialState = {}
    
    // Build the base default structure for all keys
    Object.keys(paramMapping).forEach((key) => {
      initialState[key] = {
        threshold: 10,
        selectedComparator: '',
        unit: '',
        checked: false,
        visible : form ? true : false
      };
    });

    // If not form, merge prefill data on top
    if (!form && preFillData) {
      const [processedData, distance, sensorBased] = processPreFillData(preFillData);
      setRadius(distance)
      if(sensorBased) setAlertType("model + sensor")
      Object.keys(processedData || {}).forEach((key) => {
        if (initialState[key]) {
          initialState[key] = {
            ...initialState[key],
            ...processedData[key], // override defaults
          };
        }
      });
    }
    
    return initialState
  })

  const handleChange = (
    paramKey: string,
    field: string,
    value: string | number
  ) => {
    setFormData((prevData: any) => ({
      ...prevData,
      [paramKey]: {
        ...prevData[paramKey],
        [field]: value,
      },
    }))
  }

  const handleSubmit = () => {
    const eventBody = structuredClone(eventBodyTemplate)
    const sensorHazardMap = structuredClone(sensorHazardProductMap)
    const hazardMap = structuredClone(hazardProductMpa)
    const hazardUnitMap = {
      ft: 'feet',
      sm: 'SM',
      kts: 'kts',
      F: 'degrees_f',
      C : 'degrees_c' 
    }

    eventBody.message.geometry.coordinates = [
      Number(currLocationValue.longitude),
      Number(currLocationValue.latitude),
    ]
    eventBody.message.geometry.type = 'Point'

    eventBody.message.location.id = currLocationValue.name
    eventBody.message.location.lat = currLocationValue.latitude
    eventBody.message.location.long = currLocationValue.longitude

    eventBody.message.properties.eventName = eventName
    eventBody.message.properties.externalEventName = eventName
    eventBody.message.properties.notifiables.push(notifiable)
    eventBody.message.properties.notifiables.push(TWS_API_URL + '/alerts')

    eventBody.message.properties.creationTime = new Date().toISOString()
    eventBody.message.properties.startTime = new Date(startDate).toISOString()
    eventBody.message.properties.endTime = new Date(endDate).toISOString()

    eventBody.message.properties.model_data_query = true

    Object.entries(formData).forEach(([param, data]) => {
      if (data.checked) {
        let params = {
          thresholdUnits: hazardUnitMap[data.unit],
          comparison: data.selectedComparator,
          distance: radius,
          distanceUnits: 'miles',
          threshold: data.threshold,
        }
        let hazardObject = {
          hazard: hazardMap[param],
          parameters: [params],
        }

        eventBody.message.properties.products.push(hazardObject)

        if (selectedAlertType == 'model + sensor' && param in sensorHazardMap) {
          let sensorHazardObject = {
            hazard: sensorHazardMap[param],
            parameters: [params],
          }
          eventBody.message.properties.products.push(sensorHazardObject)
        }
      }
    })
    
    addUpdateEvent(eventBody)
    .then((response) => {      
      if(response?.eventID && response?.message){
        response.message = JSON.parse(response.message)
        parentSetter(prevItems => [...prevItems, response])
        notificationBox.addMessage( 'Monitor successfully registered.', 400, 'info', false, 10 * 1000)
        updateModalState()
      }
      else{
        notificationBox.addMessage(
          response?.info || response?.message || "Unable to register the monitor.",
          400,
          'warning',
          false,
          10 * 1000
        )
      }
      
    })
    .catch((error) => {
      console.log(error)
      notificationBox.addMessage(
        'Error while registering the event.',
        400,
        'error',
        false,
        10 * 1000
      )
    })
  }

  //Retrieve custom thresholds for populating thresholds
  const populateThresholds = async () => {
    const customThresholds = await fetchUserThresholds()
    const paramHazardMapping = {
      Temperature: 'temperatureHighRange',
      'Surface Winds': 'surfaceWind',
      'Wind 250 FT': 'wind80M',
      'Surface Wind Gusts': 'surfaceWindGust',
      Ceiling: 'ceilingHeight',
      Visibility: 'visibility',
    }
    const updatedData = { ...formData }
    for (const [key, value] of Object.entries(paramHazardMapping)) {
      updatedData[key] = {
        ...updatedData[key],
        selectedComparator: customThresholds[value]['currentHighRisk'][0],
        threshold: customThresholds[value]['currentHighRisk'][1],
        unit: customThresholds[value]['currentHighRisk'][2],
      }
    }
    setFormData(updatedData)
  }

  useEffect(() => {
    if(form) populateThresholds()
  }, [])
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div
        className="absolute inset-0"
        onClick={updateModalState}
        aria-hidden="true"
      />

      <div className="relative bg-[#F8F8F8] dark:bg-gray-800 rounded-lg h-auto max-h-3/4 shadow-lg w-3/4 p-6 z-10 md:h-min">
        {/* Label and Close button */}
        <div className="flex flex-1 justify-between items-center pb-2">
          <p className="text-lg text-[#1e293b] uppercase font-semibold">{form ? "Monitor Registration" : "Active Monitor"}</p>
          <button
            className="flex text-gray-500 hover:text-gray-700  p-1 rounded-lg hover:bg-gray-200"
            onClick={updateModalState}
          >
            {' '}
            &#x2715;{' '}
          </button>
        </div>
        <hr />

        {/* Event Name | Model or Model + Sensor |Location | Radius  */}
        {/* Notifiable | Start Time | End Time */}
        <div className="grid grid-rows-2 gap-4 pt-2 pb-3">
          <div className="grid grid-cols-4 gap-2">
            <InputWithLabel
              label="Monitor Name"
              placeHolder="Monitor Name (Required)"
              type="text"
              tooltip={'Monitor name should not be a duplicate'}
              val={form ? eventName : preFillData.eventName}
              onChange={setEventName}
              edit = {form}
            />
            <DropDownWithLabel
              optionsList={alertType}
              label="Monitor Type"
              tooltip="Check if you want only Model or Model + Sensor based alerts"
              val={ selectedAlertType }
              onChange={setAlertType}
              edit = {form}
            />
            <DropDownWithLabel
              optionsList={locationList}
              label="Location"
              tooltip="Location for event."
              val={form ? currLocationValue : locationList[preFillData.location]}
              onChange={setLocationValue}
              edit = {form}
            />
            <InputWithLabel
              label="Radius"
              placeHolder="Radius"
              tooltip={'Distance from your point on map'}
              type="number"
              val={radius}
              onChange={setRadius}
              edit = {form}
            />
          </div>

          <div className="grid grid-cols-3 gap-2">
            <InputWithLabel
              label="Notifiable"
              placeHolder="Notifiable"
              type="text"
              tooltip={'List of notifiables.'}
              val={form ? notifiable : preFillData.message.properties.notifiables[0]}
              onChange={setNotifiable}
              edit = {form}
            />
            <InputWithLabel
              label="Start Time"
              placeHolder="Start Time"
              type="datetime-local"
              tooltip={'Start time of event.'}
              val={form ? startDate : formatDateForInput(preFillData.startTime)}
              onChange={setStartDate}
              edit = {form}
            />
            <InputWithLabel
              label="End Time"
              placeHolder="End Time"
              type="datetime-local"
              tooltip={'End ime of the event.'}
              val={form ? endDate : formatDateForInput(preFillData.endTime)}
              onChange={setEndDate}
              edit = {form}
            />
          </div>
        </div>
        <hr />

        {/* Hazard selection */}

        <p className="py-2 text-base text-[#1e293b] uppercase font-semibold">
          Hazard Selection
        </p>

        <div className="border border-[#94a3b8] rounded-lg h-auto max-h-1/2 overflow-y-auto">
          {/* Header */}
          <div className="flex flex-row items-center uppercase gap-2 bg-[#cbd5e1] rounded-t-lg p-2 text-sm font-semibold text-[#1e293b] divide-x divide-[#94a3b8] sticky top-0">
            <div className="w-auto flex">
              <input type="checkbox" className="invisible " />
            </div>
            <div className="flex-1 text-center">Parameter</div>
            <div className="flex-1 text-center">Comparison</div>
            <div className="flex-1 text-center">Threshold</div>
            <div className="flex-1 text-center">Units</div>
          </div>

          {/* Static Param rows */}
          <div className="flex flex-col divide-y divide-gray-300 text-base">
            {Object.entries(paramMapping).map(([key, val]) => (
              <div className={clsx("flex flex-row p-2 items-center gap-2 text-base text-[#1e293b] divide-x divide-[#94a3b8]",
                !formData[key]?.visible && "hidden"
              )}>
                {/* Checkbox for hazard selection */}
                <div className="w-auto flex">
                  <input
                    disabled = {!form}
                    type="checkbox"
                    checked={formData[key]?.checked || false}
                    onChange={(e) =>
                      handleChange(key, 'checked', e.target.checked)
                    }
                  />
                </div>

                {/* Hazard label */}
                <div
                  className={clsx('flex-1 text-center p-2 w-max', {
                    'bg-[#D6F0FF] text-blue-900 rounded-lg':
                      formData[key].checked,
                  })}
                >
                  {key}
                </div>

                {/* Comparison dropdown */}
                <div className="flex-1 text-center px-2">
                  <DropDown
                    optionsList={val['comparators']}
                    val={formData[key].selectedComparator}
                    onChange={(value) =>
                      handleChange(key, 'selectedComparator', value)
                    }
                    edit = {form}
                  />
                </div>

                {/* Threshold input field */}
                <div className="flex-1 text-center px-2">
                  <Input
                    placeHolder={'Threshold'}
                    type={'number'}
                    val={formData[key].threshold}
                    onChange={(value) => handleChange(key, 'threshold', value)}
                    label={''}
                    tooltip={''}
                    edit = {form}
                  />
                </div>

                {/* Units dropdown */}
                <div className="flex-1 text-center px-2">
                  <DropDown
                    optionsList={val['units']}
                    val={formData[key].unit}
                    onChange={(value) => handleChange(key, 'unit', value)}
                    edit = {form}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Submit + addidtion notes */}
        <div className="flex justify-between py-3">
          <div className="flex flex-col items-center">
            <div className="flex flex-row items-center">
              <img src={messagealert} className="w-10 h-10" />
              <p className="text-sm font-normal text-gray-600">
                Selected hazard will be highlighted in blue.
              </p>
            </div>
          </div>
          {
            form &&
            <button
              className={clsx(
                "px-4 py-2 uppercase text-base rounded border border-[#346F3E] transition duration-150",
                eventName !== "" && "bg-[#DFFCE6] text-green-900 hover:bg-green-400",
                eventName === "" && "bg-gray-200 text-gray-500 cursor-not-allowed"
              )}
              onClick={handleSubmit}
              disabled={eventName === ""}
            >
              Submit
            </button>
          }      
        </div>
      </div>
    </div>
  )
}

export default AlertModal
